"use client";

import React from 'react';
import { getApiBaseUrl } from '@/lib/api';
import { setToken } from '@/lib/auth';
import { loadRecaptcha, executeRecaptcha, unloadRecaptcha } from '@/lib/recaptcha';
import { useRouter, useSearchParams } from 'next/navigation';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { useLocale } from '@/contexts/LocaleContext';
import { getPathForLocale } from '@/lib/i18n';
import { useTranslations } from '@/hooks/useTranslations';

const inputClassName =
  'min-h-12 w-full rounded-2xl border border-white/10 bg-white/5 px-4 text-base text-slate-100 placeholder:text-slate-500 transition focus:border-[#ffb74d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffb74d]/45';
const primaryButtonClassName =
  'inline-flex min-h-12 w-full items-center justify-center rounded-2xl bg-[#ffb74d] px-5 py-3 text-sm font-bold text-[#3a2100] shadow-lg shadow-[#ffb74d]/20 transition hover:bg-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d] focus:ring-offset-2 focus:ring-offset-[#15192b] disabled:cursor-not-allowed disabled:opacity-60';

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const { t } = useTranslations();
  const emailRef = React.useRef<HTMLInputElement | null>(null);
  const passwordRef = React.useRef<HTMLInputElement | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  React.useEffect(() => {
    loadRecaptcha().catch(() => {
      // Recaptcha loading failed, but we continue without it
    });
    return () => unloadRecaptcha();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const email = emailRef.current?.value?.trim() ?? '';
      const password = passwordRef.current?.value ?? '';
      const captchaToken = await executeRecaptcha('login');
      
      const response = await fetch(`${getApiBaseUrl()}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, captchaToken }),
      });
      if (!response.ok) {
        let errorMessage = 'Unable to sign in. Please try again later.';
        if (response.status === 401) {
          errorMessage = 'Invalid email or password.';
        }
        try {
          const data = (await response.json()) as { message?: string };
          if (data.message) {
            errorMessage = data.message;
          }
        } catch {
          // Ignore parse errors.
        }
        throw new Error(errorMessage);
      }
      const data = (await response.json()) as { accessToken: string };
      setToken(data.accessToken);
      const next = String(searchParams.get('next') ?? '').trim();
      const target = getPathForLocale(locale, next || '/account');
      router.replace(target);
      router.refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} method="post" className="space-y-5">
      <div>
        <label htmlFor="login-email" className="mb-2 block text-sm font-semibold text-slate-200">
          {t('authFlow.fields.email')}
        </label>
        <input
          ref={emailRef}
          id="login-email"
          className={inputClassName}
          placeholder={t('authFlow.fields.emailPlaceholder')}
          required
          type="email"
          name="email"
          autoComplete="email"
          aria-invalid={message ? true : undefined}
        />
      </div>
      <div>
        <label htmlFor="login-password" className="mb-2 block text-sm font-semibold text-slate-200">
          {t('authFlow.fields.password')}
        </label>
        <div className="relative">
          <input
            ref={passwordRef}
            id="login-password"
            className={`${inputClassName} pr-12`}
            placeholder={t('authFlow.fields.passwordPlaceholder')}
            required
            type={showPassword ? 'text' : 'password'}
            name="password"
            autoComplete="current-password"
            aria-invalid={message ? true : undefined}
          />
          <button
            type="button"
            aria-label={showPassword ? t('authFlow.fields.hidePassword') : t('authFlow.fields.showPassword')}
            className="absolute right-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-300 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
            onClick={() => setShowPassword((value) => !value)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
              aria-hidden
            >
              <path d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
        </div>
      </div>
      <button
        type="submit"
        className={primaryButtonClassName}
        disabled={loading}
      >
        {loading ? t('login.signingIn') : t('auth.signIn')}
      </button>
      <div className="relative py-2">
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#15192b] px-3 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
          {t('authFlow.or')}
        </span>
        <div className="h-px bg-white/10" />
      </div>
      <GoogleSignInButton
        onSuccess={() => {
          const next = String(searchParams.get('next') ?? '').trim();
          const target = getPathForLocale(locale, next || '/account');
          router.replace(target);
          router.refresh();
        }}
        disabled={loading}
      />
      {message && (
        <p className="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100" role="alert">
          {message}
        </p>
      )}
    </form>
  );
}
